import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Typography, Button, Box, ListItemAvatar, Avatar } from '@material-ui/core'
import connectPageStyles from './connect-page.styles'
import CONSTANTS from '../../utils/constants'
import { LoadingOnBackdrop } from '../portico/loading/loading'
import FormInput from '../portico/input/form-input'

import { useDispatch, useSelector } from 'react-redux'
import PublicWrapper from '../public-wrapper-v2/public-wrapper'
import { Faq } from '../faq/faq'
import { useEntityState } from '../../stores/entity/entity.hooks'
import { getCodatIntegration } from '../../stores/async/async-actions'
import { userLogin } from '../../services/identity-service'
import { saveUserEmailAction } from '../../stores/user/user-action'
import { setSnackBar } from '../../stores/ui/ui-action'
import howDoesItWorkImage from '../../assets/how-does-it-work.svg'
import xeroIcon from '../../assets/icon-xero.svg'
import netsuiteIcon from '../../assets/icon-netsuite.svg'
import quickBooksLogo from '../../assets/logo-quickbooks.svg'
import waveLogo from '../../assets/logo-wave.svg'
import { isRestrictedEmail } from '../../utils/domain-utils'

export const ConnectPage = () => {
    const user = useSelector((state: any) => state.user)
    const { EMAIL_REGEX } = CONSTANTS
    const [submitting, setSubmitting] = useState<boolean>(false)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const classes = connectPageStyles()
    const { codatIntegrationLoadInProgress, codatIntegration } = useEntityState()
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setError
    } = useForm({
        defaultValues: { email: user.email }
    })

    const [isBlocked, setIsBlocked] = useState(false)
    const onSubmit = async (data) => {
        if (!data.email.match(EMAIL_REGEX)) {
            setError('email', { message: 'Please enter a valid email' })
            return
        }
        if (isRestrictedEmail(data.email)) {
            setError('email', {
                message: `You do not have access to this application. Contact ${CONSTANTS.LENDX_SUPPORT_EMAIL}.`
            })
            setIsBlocked(true)
            return
        }
        setIsBlocked(false)
        setSubmitting(true)
        try {
            await userLogin(data.email, 'connect')
            dispatch(saveUserEmailAction(data.email))
            setSubmitting(false)
            navigate('/verifyemail')
        } catch (e) {
            setSubmitting(false)
            dispatch(
                setSnackBar({
                    show: true,
                    message: 'Login Failed',
                    messageType: 'error'
                })
            )
        }
    }

    const getStartedClicked = () => {
        document.getElementById('email-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        document.getElementById('email')?.focus()
    }

    return (
        <>
            <LoadingOnBackdrop status={submitting} message="Logging in..." />
            <div className={classes.trapezoidBg}>
                <PublicWrapper
                    data-testid="publicWrapper"
                    backgroundClass={classes.root}
                    contentWrapperClass={classes.connectPageContent}
                    content={
                        <>
                            <div className={classes.flexBox}>
                                <div className={classes.leftBox}>
                                    <Typography style={{ fontWeight: '700' }} className={classes.bannerTitle}>
                                        Automate financial reporting
                                    </Typography>

                                    <Typography className={classes.secondaryContent}>
                                        Automate the delivery of reporting by connecting your accounting software to your First Citizens
                                        Bank account.
                                    </Typography>
                                    <div className={classes.accountingProviders}>
                                        <Typography className={classes.whatToExpect}>Accounting providers we support:</Typography>
                                        <div className={classes.companyLogo}>
                                            <img src={quickBooksLogo} className={classes.providerLogo} alt="QuickBooks" />
                                            <img src={xeroIcon} className={classes.providerLogo} alt="Xero" />
                                            <img
                                                src={netsuiteIcon}
                                                className={`${classes.providerLogo} ${classes.netSuiteLogo}`}
                                                alt="NetSuite"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={classes.rightBox}>
                                    <Box
                                        width={474}
                                        bgcolor={'#FFFFFF'}
                                        boxShadow={2}
                                        borderRadius={'4px'}
                                        padding={'27px 24px'}
                                        className={classes.rightInnerBox}
                                    >
                                        <form id="email-form" className={classes.emailForm} onSubmit={handleSubmit(onSubmit)}>
                                            <div className={classes.emailFields}>
                                                <Typography className={classes.emailHeading}>
                                                    <span className={classes.applicationText}>
                                                        {' '}
                                                        Enter your email to start or resume your connection setup{' '}
                                                    </span>
                                                </Typography>
                                                <div className={classes.emailInput}>
                                                    <FormInput
                                                        data-testid="email"
                                                        id="email"
                                                        name="email"
                                                        label={'Work Email'}
                                                        isRequired={true}
                                                        ref={register('email')}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setIsBlocked(false)
                                                            if (!e.target.value.match(EMAIL_REGEX)) {
                                                                setError('email', { message: 'Please enter a valid email' })
                                                            }
                                                        }}
                                                        debouncetime={500}
                                                        error={errors['email'] as any}
                                                        setValue={setValue}
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                className={classes.continueBtn}
                                                data-testid="continue-btn"
                                                type="submit"
                                                disabled={isBlocked || submitting}
                                            >
                                                {'Continue'}
                                            </Button>
                                        </form>
                                    </Box>
                                </div>
                            </div>
                            <div className={classes.howDoesItWorkContainer}>
                                <div className={classes.howDoesItWorkContentContainer}>
                                    <div className={classes.howDoesItWorkDetails}>
                                        <Typography className={classes.howDoesItWorkTitle}>How does it work?</Typography>
                                        <div className={classes.stepsList}>
                                            <div className={classes.stepsContainer}>
                                                <div className={classes.stepCount}>1</div>
                                                <Typography className={classes.stepText}>
                                                    Sign your Loan & Security Agreement or an amendment to your existing Loan & Security
                                                    Agreement, granting us permission to pull your accounting data.
                                                </Typography>
                                            </div>
                                            <div className={classes.stepsContainer}>
                                                <div className={classes.stepCount}>2</div>
                                                <Typography className={classes.stepText}>
                                                    Enter your email above and we’ll guide you through a quick setup to connect your
                                                    accounting software.
                                                </Typography>
                                            </div>
                                            <div className={classes.stepsContainer}>
                                                <div className={classes.stepCount}>3</div>
                                                <Typography className={classes.stepText}>
                                                    Every month our systems will automatically pull the required info through your
                                                    accounting connection.
                                                </Typography>
                                            </div>
                                            <div className={classes.stepsContainer}>
                                                <div className={classes.stepCount}>4</div>
                                                <Typography className={classes.stepText}>
                                                    No more manually compiling documents and emailing them to us every month.
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                    <Button className={classes.getStartedBtn} data-testid="get-started-btn" onClick={getStartedClicked}>
                                        Get Started
                                    </Button>
                                </div>
                                <div className={classes.howDoesItWorkGraphic}>
                                    <img
                                        className={classes.howDoesItWorkImage}
                                        src={howDoesItWorkImage}
                                        alt="Supported accounting software connections"
                                    />
                                </div>
                            </div>
                            <div className={classes.faqContainer}>
                                <Faq getStartedClicked={getStartedClicked}></Faq>
                            </div>
                        </>
                    }
                ></PublicWrapper>
            </div>
        </>
    )
}
